import { z } from 'zod';
import { FilterType } from '../../../../types/filter';
import assertUnreachable from '../../../../utils/assertUnreachable';
import { aggregationCustomMetricSchema, customMetricsSchema, periodComparisonCustomMetricSchema, } from '../customMetrics';
import { formulaTableCalcsSchema, tableCalcsSchema, } from '../tableCalcs/tableCalcs';
import { chartConfigBuiltinOnlySchema, chartConfigSchema, mergeConfigSchema, mergeSourceQueryConfigSchema, queryConfigBaseSchema, rejectAgentMergeConfig, } from '../tools/toolRunQueryArgs';
import { createToolSchema } from '../toolSchemaBuilder';
import visualizationMetadataSchema from '../visualizationMetadata';
import { FILTER_EXPRESSION_PUNCTUATED_STRING_EXAMPLE } from './examples';
import { filterExpressionDateUnits, filterExpressionOperatorDefinitions, } from './operators';
import { FILTER_EXPRESSION_MAX_LENGTH, FILTER_EXPRESSION_MAX_LITERAL_LENGTH, FILTER_EXPRESSION_MAX_RULES, FILTER_EXPRESSION_MAX_VALUES_PER_RULE, } from './parse';
const connectorInstructionByPolicy = {
    andOnly: 'Join flat rules with AND only. OR is not supported by this tool.',
    andOr: 'Join flat rules with AND or OR. Do not mix both connectors in one expression.',
};
const ruleSyntaxByArgumentCount = {
    0: '<operator>',
    1: '<operator>=<value>',
    2: '<operator>=<first value>,<second value>',
    oneOrMore: '<operator>=<value>[,<value>...]',
};
const getOperatorSyntax = (definition, argumentCount) => {
    switch (definition.argumentSyntax) {
        case 'none':
            return `${definition.operator} [0 values]`;
        case 'relativeDate':
            return `${definition.operator}=<count>{unit:<unit>,completed:<bool>} [1 count; settings required]`;
        case 'currentDate':
            return `${definition.operator}=<unit>[{toDate:true[,excludeToday:true]}] [1 unit; settings optional]`;
        case 'values':
            return `${ruleSyntaxByArgumentCount[argumentCount].replace('<operator>', definition.operator)} [${argumentCount === 'oneOrMore' ? '1+ values' : `${argumentCount} ${argumentCount === 1 ? 'value' : 'values'}`}]`;
        default:
            return assertUnreachable(definition, 'Unknown filter expression argument syntax');
    }
};
const getFilterTypeGrammar = (filterType) => {
    const operators = filterExpressionOperatorDefinitions.flatMap((definition) => {
        const argumentCount = definition.argumentCountByFilterType[filterType];
        return argumentCount === null
            ? []
            : [`- \`${getOperatorSyntax(definition, argumentCount)}\``];
    });
    const dateGuidance = filterType === FilterType.DATE
        ? `\n- Units: ${filterExpressionDateUnits.join(', ')}; completed=false includes partial, true completed only.\n- Current-period bounds: {toDate:true} ends the current week/month/quarter/year at today ("month to date"); {toDate:true,excludeToday:true} ends it at yesterday (today's partial data excluded). Not valid for days.`
        : '';
    return `### ${filterType}\nGrammar: <field> <operator form>\n${operators.join('\n')}${dateGuidance}`;
};
export const getFilterExpressionGrammarDescription = (connectorPolicy) => `- ${connectorInstructionByPolicy[connectorPolicy]}
- Bare scalars work. Double-quote reserved and whitespace/punctuation strings (apostrophes/parentheses); quote when unsure. Quoted commas/braces literal; backslash escapes. E.g. \`${FILTER_EXPRESSION_PUNCTUATED_STRING_EXAMPLE}\`.
- Limits: ${FILTER_EXPRESSION_MAX_RULES} rules; ${FILTER_EXPRESSION_MAX_VALUES_PER_RULE} values including settings/rule; ${FILTER_EXPRESSION_MAX_LITERAL_LENGTH} characters/literal; ${FILTER_EXPRESSION_MAX_LENGTH} characters/expression.

${Object.values(FilterType).map(getFilterTypeGrammar).join('\n\n')}`;
export const FILTER_EXPRESSION_GRAMMAR_DESCRIPTION = getFilterExpressionGrammarDescription('andOr');
export const FILTER_EXPRESSION_AND_ONLY_GRAMMAR_DESCRIPTION = getFilterExpressionGrammarDescription('andOnly');
export const filterExpressionInputSchema = z
    .string()
    .min(1)
    .max(FILTER_EXPRESSION_MAX_LENGTH);
export const filterExpressionsSchema = z
    .object({
    dimensions: filterExpressionInputSchema
        .nullish()
        .default(null)
        .describe('Flat filter expression for dimension fields.'),
    metrics: filterExpressionInputSchema
        .nullish()
        .default(null)
        .describe('Flat filter expression for metric fields.'),
    tableCalculations: filterExpressionInputSchema
        .nullish()
        .default(null)
        .describe('Flat filter expression for table calculations.'),
})
    .strict()
    .describe('Separate flat expressions for dimensions, metrics, and table calculations. Each category chooses AND or OR independently. Non-null categories combine implicitly with AND. For example, dimensions "D1 AND D2" and metrics "M1 OR M2" mean "(D1 AND D2) AND (M1 OR M2)", where D1, D2, M1, and M2 are complete filter rules. Use null when a category has no filters.');
export const aggregationCustomMetricExpressionSchema = aggregationCustomMetricSchema.extend({
    filters: filterExpressionInputSchema
        .nullable()
        .describe('Optional flat AND expression for conditional metric filters.'),
});
export const customMetricExpressionBaseSchema = z.discriminatedUnion('kind', [
    aggregationCustomMetricExpressionSchema,
    periodComparisonCustomMetricSchema,
]);
export const customMetricsExpressionSchema = z
    .array(customMetricExpressionBaseSchema)
    .nullable()
    .describe(customMetricsSchema.description ?? '');
export const queryConfigExpressionSchemaV2 = queryConfigBaseSchema.extend({
    customMetrics: customMetricsExpressionSchema,
    tableCalculations: tableCalcsSchema,
    filters: filterExpressionsSchema.nullable(),
});
export const queryConfigExpressionSchemaV2FormulaOnly = queryConfigExpressionSchemaV2.extend({
    tableCalculations: formulaTableCalcsSchema,
});
export const queryConfigExpressionSchemaV4 = queryConfigExpressionSchemaV2FormulaOnly;
export const mergeSourceQueryConfigExpressionSchema = queryConfigExpressionSchemaV2
    .omit({
    limit: true,
    parameters: true,
    tableCalculations: true,
})
    .describe(mergeSourceQueryConfigSchema.description ?? '');
const mergeConfigObjectSchema = mergeConfigSchema.unwrap();
export const mergeConfigExpressionSchema = mergeConfigObjectSchema
    .extend({
    additionalSources: z
        .array(z.object({
        id: z.string().min(1),
        queryConfig: mergeSourceQueryConfigExpressionSchema,
    }))
        .length(1)
        .describe(mergeConfigObjectSchema.shape.additionalSources.description ??
        ''),
})
    .nullable()
    .describe(mergeConfigSchema.description ?? '');
export const toolRunQueryExpressionArgsSchemaV2 = createToolSchema()
    .extend({
    ...visualizationMetadataSchema.shape,
    queryConfig: queryConfigExpressionSchemaV2,
    chartConfig: chartConfigSchema,
})
    .build();
export const toolRunQueryExpressionArgsSchemaV2FormulaOnly = createToolSchema()
    .extend({
    ...visualizationMetadataSchema.shape,
    queryConfig: queryConfigExpressionSchemaV2FormulaOnly,
    chartConfig: chartConfigSchema,
})
    .build();
export const toolRunQueryExpressionArgsSchemaV2Mcp = toolRunQueryExpressionArgsSchemaV2FormulaOnly.extend({
    chartConfig: chartConfigBuiltinOnlySchema,
});
export const toolRunQueryExpressionArgsSchema = createToolSchema()
    .extend({
    ...visualizationMetadataSchema.shape,
    queryConfig: queryConfigExpressionSchemaV4,
    chartConfig: chartConfigSchema,
    mergeConfig: mergeConfigExpressionSchema.default(null),
})
    .build();
export const toolRunQueryExpressionArgsSchemaV2RejectingMerge = z.preprocess(rejectAgentMergeConfig, toolRunQueryExpressionArgsSchemaV2FormulaOnly);
//# sourceMappingURL=expressionSchemas.js.map